import { ChangeEvent, forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'

import { Breakpoint, useCheckBreakpoint } from '~/utils/useBreakpoint'
import { isAllowedValue } from '~/bootcamp/Activities/utils/activities.utils'
import { InputProps } from '~/bootcamp/Activities/types/activities.types'
import { selectVideoTopicColor } from '~/bootcamp/slices/vcc-data.slice'
import { CategoryColorClasses } from '~/bootcamp/constants/bootcamp.const'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import keyboardStyles from '~/in-app-keyboard/styles/inAppKeyboard.module.css'
import InAppKeyboard from '~/in-app-keyboard/InAppKeyboard'
import { normalizeLatexAnswer } from '~/in-app-keyboard/utils/normalizeLatexAnswer'
import useInAppKeyboardLayout from '~/in-app-keyboard/hooks/useInAppKeyboard'
import { resolveInAppKeyboardLayout } from '~/in-app-keyboard/constants/inAppKeyboard.const'
import { AppLanguage } from '~/types/locale'
import { selectShouldResetInput, setShouldResetInputActionCreator } from '~/bootcamp/slices/bootcamp-activity.slice'

function hasMixedSeparators(value: string): boolean {
  return value.includes(',') && value.includes(' ')
}

function isValidUSNumber(value: string): boolean {
  if (value === '') return true

  // Accept only digits, comma, space, dot
  if (!/^[0-9,\s.-]*$/.test(value)) return false

  // Value should not start with a comma or a space
  if (value.startsWith(',') || value.startsWith(' ')) return false

  // Should not have mixed separators (comma and space)
  if (hasMixedSeparators(value)) return false

  // Reject multiple commas or spaces in a row
  if (/(,| )\1+/.test(value)) return false

  // Only one decimal point allowed
  if ((value.match(/\./g)?.length ?? 0) > 1) return false

  // Check that separators only appear in the integer part, not in decimal part
  const [, decimalPart] = value.split('.')
  if (decimalPart && /[ ,]/.test(decimalPart)) return false

  return true
}

const Input = forwardRef<HTMLInputElement | null, InputProps>((props, ref) => {
  const { locale, asPath } = useRouter()
  const isBossBattle = asPath.endsWith('quiz')

  const colorInfo: CategoryColorClasses = useAppSelector(selectVideoTopicColor)
  const {
    id,
    isError,
    isSmall,
    isWide,
    maxDigits,
    name,
    onChange,
    type = 'number',
    value,
    disabled,
    autoFocus = false,
    className,
    inputSize,
    keyboardLayoutId = null,
    ...rest
  } = props
  const inputType = locale === AppLanguage.EN ? 'text' : type
  const keyboardLayout = useInAppKeyboardLayout(keyboardLayoutId)
  const [isFocused, setIsFocused] = useState(autoFocus)
  const [currentValue, setCurrentValue] = useState<string>('')
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.SM })
  const isDesktopKeyboard = useCheckBreakpoint({ gt: Breakpoint.MD })
  const fontSize = isSmall ? 16 : isMobile ? 18 : 24
  const keyboardLayouts = useMemo(
    () => [resolveInAppKeyboardLayout(keyboardLayout, locale as AppLanguage, isDesktopKeyboard)],
    [keyboardLayout, locale, isDesktopKeyboard],
  )
  const shouldResetInput = useAppSelector(selectShouldResetInput)
  const dispatch = useAppDispatch()

  const style = {
    backgroundBlendMode: 'soft-light, normal',
    fontSize: `${fontSize}px`,
    width: getInputWidth(value, isWide),
  }

  useEffect(() => {
    // Keep the internal display state in sync when the parent clears the value
    // (e.g. on a new activity/question). Without this, the small step-working
    // inputs retain their previous digits across activity transitions.
    if (value === '') {
      setCurrentValue('')
    }
  }, [value])

  useEffect(() => {
    if (!keyboardLayout) {
      window.mathVirtualKeyboard?.hide()
    }
  }, [keyboardLayout])

  const validateInput = (newValue: string, inputName: string | undefined): void => {
    if (maxDigits && newValue?.length > maxDigits) return

    let normalizedValue = newValue
    normalizedValue = normalizedValue.replace(/[,\s]/g, '')
    if (isNaN(Number(normalizedValue))) {
      setCurrentValue(newValue)
      onChange(newValue, inputName)
      return
    }
    if (isAllowedValue(newValue)) {
      if (locale === AppLanguage.EN) {
        if (isValidUSNumber(newValue)) {
          setCurrentValue(newValue)
          onChange(normalizedValue, inputName)
        }
      } else {
        setCurrentValue(newValue)
        onChange(newValue, inputName)
      }
    }
  }

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value: newValue, name: inputName } = event.target
    validateInput(newValue, inputName)
  }

  const classNameInput =
    `inline-block rounded-small border-2 outline-0 no-input-arrows shadow-input ${
      !isError && 'sm:hover:bg-purple-light-deep'
    } ` +
    'text-center text-purple font-semibold placeholder-purple-light disabled:cursor-not-allowed ' +
    `${getInputBorder(Boolean(value), isError, isFocused, colorInfo.borderClass)} ${className} ` +
    `${
      keyboardLayout
        ? `${keyboardStyles['input-bootcamp-math-field']} ${keyboardStyles['input-common-math-field']}`
        : ''
    }`

  const onResetInput = useCallback(() => {
    if (currentValue !== '') {
      setCurrentValue('')
      onChange('', name)
    }
  }, [currentValue, name, onChange])

  useEffect(() => {
    if (shouldResetInput) {
      onResetInput()
      dispatch(setShouldResetInputActionCreator(false))
    }
  }, [dispatch, onResetInput, shouldResetInput])

  return (
    <>
      {keyboardLayout ? (
        <InAppKeyboard
          autoFocus={autoFocus}
          className={classNameInput}
          focused={isFocused}
          isBossBattle={isBossBattle}
          layouts={keyboardLayouts}
          mathFieldClassName={classNameInput}
          maxDigits={maxDigits}
          onChange={(val: string) => {
            // The math-field emits LaTeX; graders and validation expect the plain string a
            // native input would produce ('\text{b}' -> 'b', '3{,}5' -> '3,5').
            validateInput(normalizeLatexAnswer(val), name)
          }}
          onEnterPress={props.onEnter}
          setFocused={setIsFocused}
          style={style}
          type={inputType}
          value={value}
        />
      ) : (
        <input
          autoFocus={autoFocus}
          className={classNameInput}
          disabled={disabled}
          id={id}
          name={name}
          onBlur={() => setIsFocused(false)}
          onChange={handleOnChange}
          onFocus={() => setIsFocused(true)}
          onWheel={(event) => event.currentTarget.blur()}
          ref={ref}
          step="any"
          style={style}
          type={inputType}
          value={currentValue}
          {...rest}
        />
      )}
    </>
  )
})

function getInputBorder(
  hasValue: boolean,
  isError: boolean | undefined,
  isFocused: boolean,
  borderClass: string,
): string {
  if (isError && hasValue) {
    return '!text-white bg-red border-red'
  } else if (isFocused) {
    return 'border-black-light'
  } else {
    return `bg-white ${borderClass}`
  }
}

function getInputWidth(value: string, isWide: boolean | undefined): string {
  if (isWide) {
    if (value.length > 8) {
      const scalar = value.length - 8
      return `calc(8ch + (1ch * ${scalar}) + 16px)`
    } else {
      return 'calc(8ch + 16px)'
    }
  }

  const scalar = value.length >= 1 ? value.length : 1
  return `calc((1ch * ${scalar}) + 16px)`
}

Input.displayName = 'Input'

export default Input
